import { deriveNaturalTrace } from '../../model/natural/machine';
import { projectNaturalDivisionTrace } from './divisionTraceProjection';
import { toShatterOffset, toShatterSpread } from './shatter';
import { momentKey, stepMoment } from '../primitiveMoment';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

/** Mechanically derived sample cycles after explicit skips, not additions to the stored execution trace. */
export function withQuotientGrowthIllustration(
    config: Config, steps: readonly PrimitiveStepState[], bands: number | undefined,
    /** Sampled cycles follow every shown comparison, so any bound abbreviates their checks. */
    fullComparisons?: number,
): readonly PrimitiveStepState[] {
    if (bands === undefined) return steps;
    const cutoff = steps.at(-1);
    const resting = cutoff?.keyframes[0]?.wheels;
    const quotient = resting?.find(wheel => wheel.role === 'quotient' && wheel.variant === undefined);
    const divisor = resting?.find(wheel => wheel.role === 'divisor' && wheel.variant === undefined);
    const dividend = resting?.find(wheel => wheel.role === 'dividend' && wheel.variant === undefined);
    const remainder = resting?.find(wheel => wheel.role === 'remainder' && wheel.variant === undefined);
    if (config.natural.radix !== 10 || !Number.isInteger(bands) || bands < 3 || bands > 10 || cutoff?.stepKey !== 'shatter-quotient'
        || !resting || !quotient || (quotient.valueKind !== undefined && quotient.valueKind !== 'natural')
        || !divisor || (divisor.valueKind !== undefined && divisor.valueKind !== 'natural')
        || !dividend || (dividend.valueKind !== undefined && dividend.valueKind !== 'natural')
        || !remainder || (remainder.valueKind !== undefined && remainder.valueKind !== 'natural')
        || divisor.value !== 0 || quotient.powers.length >= bands) {
        throw new Error('Quotient growth illustration requires a decimal bounded zero-divisor trace and 3 to 10 bands beyond its Quotient.');
    }
    const integrated = cutoff.keyframes[0]!.booleanRod?.preserveNaturalAxis === true;
    const replace = (wheel: PrimitiveWheelState) => resting.map(entry => entry === quotient ? wheel
        : integrated && entry === divisor ? { ...entry, bandOpacityByPower: undefined } : entry);
    let booleanRod = cutoff.keyframes[0]!.booleanRod;
    const growth: PrimitiveStepState[] = [];
    let expanded = quotient;
    const remainderValue = remainder.powers.reduce((value, power) =>
        value + (remainder.countedThroughDigitByPower?.[power] ?? 0) * 10 ** power, 0);
    const initializationTrace = deriveNaturalTrace({ operation: 'addition', radix: 10,
        operandOne: dividend.value, operandTwo: 0, operandSchedulingMode: 'grouped' });
    const subtractionTrace = deriveNaturalTrace({ operation: 'subtraction', radix: 10,
        operandOne: remainderValue, operandTwo: 0, operandSchedulingMode: 'grouped' },
    { preResultInitialization: 'resident' });
    for (let power = quotient.powers.length; power < bands; power++) {
        const before = 10 ** power - 1;
        const after = 10 ** power;
        const starting = { ...quotient, value: before,
            powers: Array.from({ length: power }, (_, index) => index),
            countedThroughDigitByPower: Object.fromEntries(
                Array.from({ length: power }, (_, index) => [index, 9])),
        };
        const quotientIncrementTrace = deriveNaturalTrace({ operation: 'addition', radix: 10,
            operandOne: before, operandTwo: 1, operandSchedulingMode: 'grouped' },
        { preResultInitialization: 'resident' });
        const projected = projectNaturalDivisionTrace({ config, dividend: dividend.value, divisor: 0,
            residentQuotient: true,
            ...(integrated ? { divisionComparison: 'integrated' as const } : {}),
            ...(fullComparisons === undefined ? {} : { fullComparisons: 0 }),
            trace: { dividend: dividend.value, divisor: 0, initializationTrace,
                cycles: [{ cycleIndex: 1, remainderBefore: remainderValue, remainderAfter: remainderValue,
                    quotientBefore: before, quotientAfter: after, subtractionTrace, quotientIncrementTrace }],
                quotient: after, remainder: remainderValue,
                outcome: { outcomeKind: 'failed', failureKind: 'runaway-zero-divisor',
                    cutoff: { cutoffKind: 'cycle-budget', limit: 1 } },
            },
        });
        const cycle = projected.filter(step => {
            const frame = step.moment?.frames[0];
            return frame?.kind === 'subtraction' || frame?.kind === 'quotient-increment'
                || integrated && frame?.kind === 'division-comparison' && frame.ordinal?.index === 1;
        });
        const final = cycle.at(-1)?.keyframes.at(-1)?.wheels.find(wheel =>
            wheel.role === 'quotient' && wheel.variant === undefined);
        if (!final || (final.valueKind !== undefined && final.valueKind !== 'natural')
            || final.powers.length !== power + 1) throw new Error('Sampled Quotient cycle did not expand by one band.');
        expanded = { ...final, value: after };
        growth.push({
            ...momentKey(stepMoment('illustrative-quotient-band', { power })),
            label: `Later cycles: ${power + 1} Quotient bands`,
            description: `Skip to Quotient ${before}, then show one complete zero-removal and increment cycle to ${after}.`,
            illustrativeQuotientPower: power,
            cameraFocus: { stationKey: 'working:quotient:0', viewKey: 'working:quotient:0',
                roles: ['quotient'], targetRole: 'quotient', power: 0 },
            keyframes: [
                { wheels: replace(starting), ...(booleanRod ? { booleanRod } : {}), transition: 'instant', events: [] },
                ...cycle.flatMap(step => step.keyframes),
                { wheels: replace(expanded), ...(integrated ? { booleanRod: cycle.at(-1)!.keyframes.at(-1)!.booleanRod } : {}),
                    transition: 'instant', events: [] },
            ],
        });
        booleanRod = cycle.at(-1)!.keyframes.at(-1)!.booleanRod;
    }
    const shattered = { ...expanded, wheelOpacity: 0,
        positionByPower: Object.fromEntries(expanded.powers.map((power, memberIndex) => {
            const offset = toShatterOffset({ memberIndex, scatter: 1, spread: toShatterSpread(config) });
            return [power, expanded.position.map((value, axis) => value + offset[axis]!) as [number, number, number]];
        })),
    };
    return [...steps.slice(0, -1), ...growth, { ...cutoff, keyframes: [
        { ...cutoff.keyframes[0]!, wheels: replace(expanded), ...(booleanRod ? { booleanRod } : {}) },
        { ...cutoff.keyframes[1]!, wheels: replace(shattered), ...(booleanRod ? { booleanRod } : {}) },
    ] }];
}
